import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList } from 'react-native';
import { RefreshControl } from 'react-native-gesture-handler';

import { useNavigation } from '@react-navigation/native';

import { BottomFooterRG } from '@/components/BottomFooterRG';
import { ds } from '@/components/lib/design.system';
import { TraditionalMatchupCard } from '@/components/sbk-traditional-matchup-card/TraditionalMatchupCard';
import { TEAM_ROW_HEIGHT } from '@/components/sbk-traditional-matchup-card/parts/TeamInfoRow';
import { hasThreeWayResult } from '@/components/sbk-traditional-matchup-card/utils';
import { EventWithMarkets } from '@/feature/event-details-sbk/types';
import { useEventsTabsStore } from '@/hooks/use-events-tabs-store';
import { useForceRefresh } from '@/hooks/use-force-refresh';
import { SweepsLobbyTabNavigationProp } from '@/navigation/sweeps/types';
import { designSystem } from '@/styles/styles';
import { showScrollIndicator } from '@/utils/constants-platform-specific';

import { LeagueMatchupData } from '../hooks/use-league-traditional-matchup-data';
import { EventListSkeleton } from './EventListSkeleton';
import { NoMarketsMessage } from './NoMarketsMessage';

const keyExtractor = (item: EventWithMarkets) => item.id;

const FIRST_ITEM_HEIGHT = 219;
const ITEM_HEIGHT = 207;
const TIE_ROW_HEIGHT = TEAM_ROW_HEIGHT + 3; // row height + gap (s3)

const ITEM_HEIGHTS = {
    standard: { first: FIRST_ITEM_HEIGHT, rest: ITEM_HEIGHT },
    threeWay: { first: FIRST_ITEM_HEIGHT + TIE_ROW_HEIGHT, rest: ITEM_HEIGHT + TIE_ROW_HEIGHT },
};

type EventListProps = {
    leagueId: string;
    matchupData: LeagueMatchupData;
    isLive?: boolean;
    header?: ReactNode;
};

export const EventList = ({ leagueId, matchupData, isLive = false, header }: EventListProps) => {
    const { t } = useTranslation('lobby_sbk');
    const navigation = useNavigation<SweepsLobbyTabNavigationProp>();
    const { events, hasAnyAvailableMarkets, refetch, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
        matchupData;
    const { forceRefreshing, onRefresh } = useForceRefresh(refetch);

    const { first: firstItemHeight, rest: itemHeight } = useMemo(() => {
        const firstEvent = events?.[0];
        const hasTieRow = firstEvent
            ? hasThreeWayResult(firstEvent.popular_markets.map(cat => ({ marketType: cat.market_type })))
            : false;
        return hasTieRow ? ITEM_HEIGHTS.threeWay : ITEM_HEIGHTS.standard;
    }, [events]);

    const navigateToEvent = useCallback(
        (event: EventWithMarkets, eventIndex: number) => {
            useEventsTabsStore.getState().actions.setSelectedTab(eventIndex);
            navigation.navigate('SbkEventsSheets', {
                eventId: event.id,
                contextKey: new Date().getTime().toString(),
                leagueId: event?.league?.id,
            });
        },
        [navigation]
    );

    const renderItem = useCallback(
        ({ item, index }: { item: EventWithMarkets; index: number }) => {
            return (
                <TraditionalMatchupCard
                    event={item}
                    showHeader={index === 0}
                    onPress={() => navigateToEvent(item, index)}
                />
            );
        },
        [navigateToEvent]
    );

    return (
        <>
            {isLoading ? (
                <>
                    {header}
                    <EventListSkeleton leagueId={leagueId} />
                </>
            ) : hasAnyAvailableMarkets ? (
                <>
                    {header}
                    <FlatList
                        data={events}
                        renderItem={renderItem}
                        keyExtractor={keyExtractor}
                        showsVerticalScrollIndicator={showScrollIndicator}
                        refreshControl={
                            <RefreshControl
                                enabled
                                onRefresh={onRefresh}
                                refreshing={forceRefreshing}
                                colors={[designSystem.colors.neutral700]}
                                tintColor={ds.colorValue.icon.primary}
                            />
                        }
                        ListFooterComponent={
                            <>
                                {isFetchingNextPage ? (
                                    <EventListSkeleton leagueId={leagueId} count={1} hideHeader />
                                ) : null}
                                <BottomFooterRG footerMarginTop="s64" paddingHorizontal="s16" />
                            </>
                        }
                        initialNumToRender={4}
                        removeClippedSubviews={true}
                        getItemLayout={(_data, index) => ({
                            length: index === 0 ? firstItemHeight : itemHeight,
                            offset: index === 0 ? 0 : firstItemHeight + (index - 1) * itemHeight,
                            index,
                        })}
                        onEndReachedThreshold={0.5}
                        onEndReached={() => !isFetchingNextPage && hasNextPage && fetchNextPage()}
                    />
                </>
            ) : (
                <NoMarketsMessage message={t(isLive ? 'no_live_markets_available' : 'markets_available_soon')} />
            )}
        </>
    );
};
